/**
 * User API
 * Handles user profile, notifications, and welcome status
 * Endpoints: /api/user/*
 */
import apiClient, { createAuthConfig, isMockApi } from "../client";
import {
  mockGetProfile,
  mockUpdateProfile,
  mockGetNotifications,
  mockMarkNotificationRead,
  mockMarkAllNotificationsRead,
  mockCheckFirstTime,
  mockMarkWelcomed,
  mockClaimSignupCredits,
} from "../mocks";

/**
 * Get user profile information
 * @param {string} token - Clerk session token
 * @returns {Promise<Object>} - User profile data
 */
export const getProfile = async (token) => {
  if (isMockApi) return mockGetProfile();
  const response = await apiClient.get('/api/user/profile', createAuthConfig(token));
  return response.data;
};

/**
 * Update user profile
 * @param {string} token - Clerk session token
 * @param {Object} updateData - Profile update data
 * @returns {Promise<Object>}
 */
export const updateProfile = async (token, updateData) => {
  if (isMockApi) return mockUpdateProfile();
  const response = await apiClient.put('/api/user/profile', updateData, createAuthConfig(token));
  return response.data;
};

/**
 * Get user notifications
 * @param {string} token - Clerk session token
 * @param {boolean} unreadOnly - Only get unread notifications
 * @param {number} limit - Maximum number of notifications
 * @returns {Promise<Object>}
 */
export const getNotifications = async (token, unreadOnly = false, limit = 50) => {
  if (isMockApi) return mockGetNotifications();
  const params = new URLSearchParams({
    unread_only: unreadOnly.toString(),
    limit: limit.toString(),
  });
  const response = await apiClient.get(`/api/user/notifications?${params}`, createAuthConfig(token));
  return response.data;
};

/**
 * Mark notification as read
 * @param {string} token - Clerk session token
 * @param {number} notificationId - Notification ID
 * @returns {Promise<Object>}
 */
export const markNotificationRead = async (token, notificationId) => {
  if (isMockApi) return mockMarkNotificationRead();
  const response = await apiClient.put(
    `/api/user/notifications/${notificationId}/read`,
    {},
    createAuthConfig(token)
  );
  return response.data;
};

/**
 * Mark all notifications as read
 * @param {string} token - Clerk session token
 * @returns {Promise<Object>}
 */
export const markAllNotificationsRead = async (token) => {
  if (isMockApi) return mockMarkAllNotificationsRead();
  const response = await apiClient.put('/api/user/notifications/read-all', {}, createAuthConfig(token));
  return response.data;
};

/**
 * Check if user is first-time (not welcomed yet)
 * @param {string} token - Clerk session token
 * @returns {Promise<Object>} - { success, is_first_time, message }
 */
export const checkFirstTime = async (token) => {
  if (isMockApi) return mockCheckFirstTime();
  const response = await apiClient.get('/api/user/check-first-time', createAuthConfig(token));
  return response.data;
};

/**
 * Mark user as welcomed (after popup is closed)
 * @param {string} token - Clerk session token
 * @returns {Promise<Object>}
 */
export const markWelcomed = async (token) => {
  if (isMockApi) return mockMarkWelcomed();
  const response = await apiClient.put('/api/user/mark-welcomed', {}, createAuthConfig(token));
  return response.data;
};

/**
 * Claim signup credits (first-time user)
 * @param {string} token - Clerk session token
 * @returns {Promise<Object>} - { success, newBalance }
 */
export const claimSignupCredits = async (token) => {
  if (isMockApi) return mockClaimSignupCredits();
  const response = await apiClient.post('/api/user/claim-signup-credits', {}, createAuthConfig(token));
  return response.data;
};

export default {
  getProfile,
  updateProfile,
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  checkFirstTime,
  markWelcomed,
  claimSignupCredits,
};





